import Head from 'next/head'
import Link from 'next/link'
import BrandWordmark from '../components/BrandWordmark'

export default function PrivacyPage() {
  return (
    <>
    <Head>
      <title>Privacy Policy — SkillBets</title>
      <meta name="description" content="Read the SkillBets Privacy Policy to understand how we collect, use, and protect your personal information." />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="SkillBets" />
      <meta property="og:locale" content="en_ZA" />
      <meta property="og:title" content="Privacy Policy — SkillBets" />
      <meta property="og:description" content="Read the SkillBets Privacy Policy to understand how we collect, use, and protect your personal information." />
      <link rel="canonical" href="https://skillbets.co.za/privacy/" />
      <meta property="og:url" content="https://skillbets.co.za/privacy" />
      <meta property="og:image" content="https://skillbets.co.za/og-image.png" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@SkillBetsZA" />
      <meta name="twitter:title" content="Privacy Policy — SkillBets" />
      <meta name="twitter:description" content="Read the SkillBets Privacy Policy to understand how we collect, use, and protect your personal information." />
      <meta name="twitter:image" content="https://skillbets.co.za/og-image.png" />
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      <link href="https://fonts.googleapis.com/css2?family=Archivo:wght@800&display=swap" rel="stylesheet" />
    </Head>
    <main className="min-h-screen bg-gray-900 text-white">
      {/* Compliance ribbon */}
      <div className="w-full bg-black/40 px-4 py-2 text-center text-[11px] text-white/70">
        Simulation & analytics only • No cash wagering or payouts • SkillBets is not a bookmaker
      </div>

      {/* Header */}
      <header className="border-b border-gray-800 bg-gray-900">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-4">
          <Link href="/" className="flex items-center" aria-label="SkillBets home">
            <BrandWordmark />
          </Link>
          <Link
            href="/"
            className="rounded-xl bg-[#3EA8F5] px-3 py-2 text-xs font-bold text-gray-900 hover:brightness-110"
            data-testid="button-back-home"
          >
            Back to Home
          </Link>
        </div>
      </header>

      {/* Body */}
      <article className="mx-auto max-w-5xl px-4 py-10">
        <h1 className="text-3xl sm:text-4xl font-extrabold">Privacy Policy</h1>
        <p className="mt-3 max-w-3xl text-white/70">
          This Privacy Policy explains how SkillBets ("we", "us") collects, uses, discloses, and safeguards your
          information when you use our website and services (the "Service"). We follow the Protection of Personal
          Information Act, 2013 (POPIA) and process personal information lawfully, reasonably, and securely.
        </p>
        <p className="mt-2 text-xs text-white/50" suppressHydrationWarning>Last updated: 2025-01-01</p>

        <section className="prose prose-invert mt-8 max-w-none">
          <h2 className="text-2xl font-bold mt-8 mb-4">1. Who We Are and Scope</h2>
          <p className="text-white/80">
            SkillBets provides a simulation and analytics platform for sports predictions using virtual currency only. This
            Policy applies to your use of the Service. If you do not agree with this Policy, please do not use the
            Service.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-4">2. Information We Collect</h2>
          <ul className="list-disc pl-5 text-white/80 space-y-2">
            <li>
              <strong>Account Information:</strong> Username, Name, Surname, Email, Telephone Number, Date of Birth (18+ check),
              Password (stored securely using hashing), and optional Promo Code.
            </li>
            <li>
              <strong>Service Usage Data:</strong> Simulated picks, confidence settings, outcomes, virtual currency balances and
              adjustments, leaderboards, preferences, and in-app interactions.
            </li>
            <li>
              <strong>Device and Log Data:</strong> IP address, device and browser type, operating system, referring pages, pages
              visited, timestamps, and diagnostic logs.
            </li>
            <li>
              <strong>Communications:</strong> Support requests, emails, and other messages you send to us.
            </li>
            <li>
              <strong>Cookies and Similar Technologies:</strong> We use essential, preference, and analytics cookies. See the
              Cookies section below for choices.
            </li>
          </ul>

          <h2 className="text-2xl font-bold mt-8 mb-4">3. How We Use Information (Purpose Specification)</h2>
          <ul className="list-disc pl-5 text-white/80 space-y-2">
            <li>Provide, operate, and maintain the Service (account creation, authentication, age eligibility).</li>
            <li>Process simulations, keep ledgers of virtual currency, and display leaderboards.</li>
            <li>Provide analytics features, insights, and product improvements.</li>
            <li>Monitor security, prevent abuse and fraud, and enforce our Terms & Conditions.</li>
            <li>Communicate with you about your account, important changes, and support responses.</li>
            <li>Comply with applicable laws and respond to lawful requests from authorities.</li>
          </ul>

          <h2 className="text-2xl font-bold mt-8 mb-4">4. Lawful Grounds (POPIA)</h2>
          <p className="text-white/80">
            We process personal information under POPIA based on one or more of the following: your consent; processing
            necessary to conclude or perform in terms of our agreement with you; compliance with law; legitimate
            interests pursued by us or a third party, balanced against your rights (for example, to ensure security and
            improve the Service); or because the information was made public by you.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-4">5. How We Share Information</h2>
          <ul className="list-disc pl-5 text-white/80 space-y-2">
            <li>
              <strong>Service Providers:</strong> Hosting, analytics, email delivery, customer support, and payment processors for
              subscriptions. They act on our instructions and are bound by confidentiality and security obligations.
            </li>
            <li>
              <strong>Legal and Compliance:</strong> When required by law, regulation, or legal process, or to protect rights,
              safety, and integrity of users and the Service.
            </li>
            <li>
              <strong>Business Transfers:</strong> In connection with a merger, acquisition, or asset sale, subject to continued
              protection of your information.
            </li>
          </ul>

          <h2 className="text-2xl font-bold mt-8 mb-4">6. International Transfers</h2>
          <p className="text-white/80">
            Your information may be transferred to and stored on servers located outside South Africa. When we transfer
            information internationally, we use appropriate safeguards (for example, contractual commitments) to protect
            your personal information, consistent with POPIA.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-4">7. Security Safeguards</h2>
          <p className="text-white/80">
            We implement technical and organisational measures designed to protect personal information against
            unauthorised access, loss, misuse, or alteration. However, no method of transmission or storage is completely
            secure, and we cannot guarantee absolute security.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-4">8. Retention</h2>
          <p className="text-white/80">
            We retain personal information for as long as needed to provide the Service, comply with legal obligations,
            resolve disputes, and enforce our agreements. Retention periods vary depending on the type and purpose of
            information. When no longer required, we securely delete or de-identify information.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-4">9. Your Rights (POPIA Data Subject Participation)</h2>
          <p className="text-white/80">You have rights under POPIA, subject to certain limitations:</p>
          <ul className="list-disc pl-5 text-white/80 space-y-2">
            <li>Request access to the personal information we hold about you.</li>
            <li>Request correction, update, or deletion of inaccurate personal information.</li>
            <li>Object to or restrict certain processing, and withdraw consent where processing is based on consent.</li>
            <li>Request a record or description of your personal information we hold.</li>
            <li>Lodge a complaint with the Information Regulator (South Africa) if you believe your rights are infringed.</li>
          </ul>
          <p className="text-white/80 mt-2">
            To exercise your rights, contact us at <a className="text-[#3EA8F5] underline" href="mailto:support@skillbets.co.za">support@skillbets.co.za</a>.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-4">10. Cookies and Similar Technologies</h2>
          <p className="text-white/80">
            We use cookies to keep you signed in, remember preferences, and understand how the Service is used. You can
            control cookies through your browser settings and, where applicable, our cookie banner. Disabling certain
            cookies may affect functionality.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-4">11. Children</h2>
          <p className="text-white/80">
            The Service is intended for adults 18+ only. We do not knowingly collect personal information from children.
            If you believe a child has provided us information, contact us and we will take appropriate steps.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-4">12. Communications Preferences</h2>
          <p className="text-white/80">
            We may send you transactional communications about your account. You can opt out of non-essential messages
            (such as product updates) by using unsubscribe links or by contacting support. We may still send important
            notices about your account or our terms.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-4">13. Changes to this Policy</h2>
          <p className="text-white/80">
            We may update this Policy to reflect changes in the Service or legal requirements. When we make material
            changes, we will provide reasonable notice (for example, by email or a notice on the Service). Your
            continued use after changes take effect constitutes acceptance of the updated Policy.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-4">14. Contact Us</h2>
          <p className="text-white/80">
            For questions or requests regarding this Policy or your personal information, contact our Privacy Officer at
            <a className="text-[#3EA8F5] underline ml-1" href="mailto:support@skillbets.co.za">support@skillbets.co.za</a>.
            You may also contact the Information Regulator (South Africa) through official channels available on its
            website.
          </p>
        </section>
      </article>
    </main>
    </>
  );
}
